import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  input,
  OnInit,
  output,
  Signal,
  signal,
} from "@angular/core";
import { FormControl, UntypedFormBuilder, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { Temporality } from "../../update-notice/model/temporality";
import moment, { invalid } from "moment";
import { BehaviorSubject, combineLatest, filter, map, of, startWith, tap } from "rxjs";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FileUploader } from "ng2-file-upload";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormatBytesPipe } from "src/app/shared/pipes/format-bytes.pipe";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSelectChange } from "@angular/material/select";
import { ConfigService } from "src/app/utils/config.service";
import { AisProduct } from "../enums/AisProducts";
import {
  CANCEL_DATE_VALIDATOR_ASYNC,
  NOTAM_ITEM_E_PATTERN,
  NOTAM_NRC_FIELDS_VALIDATOR_ASYNC,
  NOTAM_RC_FIELDS_VALIDATOR_ASYNC,
  fourDigitYearValidator,
  fourUppercaseLettersValidator,
  positiveNumberValidator,
  uppercaseLetterValidator,
  verticalLimitsValidator,
} from "./validators";
import { NotamType } from "../enums/NotamTypes";
import { PropertiesService } from "src/app/properties/services/properties.service";
import { Property } from "../enums/DatastoreProperty";
import { UpdateNoticeDetailsDTO } from "src/app/update-notice/model/UpdateNoticeDetailsDTO";
import { AiracType } from "../enums/AiracTypes";
import { time } from "console";

@Component({
  selector: "app-default-form",
  standalone: false,
  templateUrl: "./default-form.component.html",
  styleUrl: "./default-form.component.scss",
})
export class DefaultFormComponent implements OnInit, AfterViewInit {
  displayDueDateSection = input<boolean>(true);

  formReadonly = input<boolean>(false);

  formDisabled = input<boolean>(false);

  mode = input.required<"new" | "edit">();

  dueDateChanged = output<string>();

  private _updateNoticeData = new BehaviorSubject<UpdateNoticeDetailsDTO | null>(null);

  @Input("updateNoticeData") set updateNoticeData(value: UpdateNoticeDetailsDTO) {
    if (value) {
      this._updateNoticeData.next(value);
    }
  }

  aisProductChanged = output<AisProduct>();

  onAttachmentClicked = output<string>();

  private _destroyRef = inject(DestroyRef);

  private _formBuilder = inject(UntypedFormBuilder);

  private _snackbar = inject(MatSnackBar);

  private _route = inject(ActivatedRoute);

  private _router = inject(Router);

  private _dsPropsService = inject(PropertiesService);

  private _maxFileSize = (ConfigService.settings?.attachmentFileSizeLimit || 100) * 1024 * 1024;

  private _airacInterval = 28;

  private _reloader$ = new BehaviorSubject(false);

  notamTypes = [
    { label: "NOTAM N", subtitle: "New NOTAM", value: NotamType.N },
    { label: "NOTAM R", subtitle: "Replace existing NOTAM", value: NotamType.R },
    { label: "NOTAM C", subtitle: "Cancel existing NOTAM", value: NotamType.C },
  ];

  aisProductEnum = AisProduct;

  aisProducts = [
    { label: "AIP Amendment", value: AisProduct.AMDT },
    { label: "AIP Supplement", value: AisProduct.SUP },
    { label: "NOTAM", value: AisProduct.NOTAM },
    { label: "AIC", value: AisProduct.AIC },
  ];

  temporalities = [
    {
      label: "Permanent",
      subtitle: "Until Further Notice",
      value: Temporality.PERMANENT,
      disableOn: [AisProduct.SUP, AisProduct.NOTAM],
    },
    {
      label: "Temporary",
      subtitle: "With specific Period",
      value: Temporality.TEMPORARY,
      disableOn: [AisProduct.AMDT],
    },
  ];

  airacTypes = [
    { label: "Non-AIRAC", value: AiracType.NON_AIRAC, displayOn: [AisProduct.AMDT] },
    { label: "AIRAC", value: AiracType.AIRAC, displayOn: [AisProduct.AMDT, AisProduct.SUP] },
    { label: "Double AIRAC", value: AiracType.DOUBLE_AIRAC, displayOn: [AisProduct.AMDT] },
  ];

  selectedAisProduct = new FormControl(this._route.snapshot.params?.aisProduct);

  amdtNoticePeriod = new BehaviorSubject<number>(0);

  amdtNonAiracNoticePeriod = new BehaviorSubject<number>(0);

  amdtDoubleNoticePeriod = new BehaviorSubject<number>(0);

  supNoticePeriod = new BehaviorSubject<number>(0);

  timezonePreferences = new BehaviorSubject([]);

  validDate: moment.Moment = null;

  startAt: moment.Moment = null;

  dueDate: string | null = null;

  isEdit = computed(() => this.mode() === "edit");

  form = this._formBuilder.group({
    aisProduct: this.selectedAisProduct,
    name: [null, Validators.required],
    description: [null],
    effectiveDate: [null, [Validators.required]],
    cancelDate: [null, null, CANCEL_DATE_VALIDATOR_ASYNC],
    effectiveTime: [null],
    cancelTime: [null],
    temporality: [null],
    airacType: [null],
    timezone: ["UTC"],
    // NOTAM related fields
    estimated: [null],
    notamType: [null], // The values for this fields are N, R, C
    referredSeries: [null, [uppercaseLetterValidator], [NOTAM_RC_FIELDS_VALIDATOR_ASYNC]],
    referredNumber: [null, [positiveNumberValidator], [NOTAM_RC_FIELDS_VALIDATOR_ASYNC]],
    referredYear: [null, [fourDigitYearValidator], [NOTAM_RC_FIELDS_VALIDATOR_ASYNC]],
    notamText: [null, Validators.pattern(NOTAM_ITEM_E_PATTERN), NOTAM_NRC_FIELDS_VALIDATOR_ASYNC],
    firAerodromeAirfield: [null, [fourUppercaseLettersValidator], [NOTAM_NRC_FIELDS_VALIDATOR_ASYNC]],
    // lowerLimit: [null, Validators.pattern(LOWER_LIMIT_PATTERN)],
    lowerLimit: [null, verticalLimitsValidator("lower")],
    upperLimit: [null, verticalLimitsValidator("upper")],
    // AMDT related fields
    includeAmdtDetails: [false], // This field is to control showing the 3 fields for AMDT Details. Maybe not neccesary to send it to the backend
    sectionsToBeAmended: [null],
    paragraphToBeAmended: [null],
    textToBeAmended: [null],
  });

  effectiveTime = new FormControl<string | null>(null);

  cancelTime = new FormControl<string | null>(null);

  uploader = new FileUploader({
    url: "",
    maxFileSize: this._maxFileSize,
  });

  uploadedFiles: string[] = [];

  filesToBeDeleted: string[] = [];

  attachmentsModified = signal(false);

  verticalLimitsErrorMsgMap(type: "lower" | "upper"): string {
    const control = type === "lower" ? this.fc.lowerLimit : this.fc.upperLimit;
    const map = {
      WRONG_REF: "Invalid reference. Use AGL or AMSL (e.g., 100M AGL)",
      FL_FORMAT: "Flight Levels must be 3 digits (e.g., FL100)",
      MISSING_UNIT: "Unit missing (e.g., 100FT or 100M)",
      INVALID_UNIT: "Use FT or M (e.g., 100FT)",
      MISSING_REF: "Reference missing (e.g., 100FT AGL)",
      INVALID_REF: `Invalid value. Use ${control?.errors?.["allowed"]}`,
      INVALID_DIGITS: "Digits must be 5 or less (e.g., 10000FT)",
    };
    return map[control.errors?.errorType] || "Invalid aviation format.";
  }

  get fc() {
    return this.form.controls;
  }

  get isTemporary() {
    if (!this.fc.temporality.value) return null;
    return this.fc.temporality.value[0] === Temporality.TEMPORARY;
  }

  get lowerLimitError(): string {
    return this.verticalLimitsErrorMsgMap("lower");
  }

  get upperLimitError(): string {
    return this.verticalLimitsErrorMsgMap("upper");
  }

  get isAMDT(): boolean {
    if (!this.fc.aisProduct.value) return null;
    return this.fc.aisProduct.value === AisProduct.AMDT;
  }

  get isSUP(): boolean {
    if (!this.fc.aisProduct.value) return null;
    return this.fc.aisProduct.value === AisProduct.SUP;
  }

  get isNOTAM(): boolean {
    if (!this.fc.aisProduct.value) return null;
    return this.fc.aisProduct.value === AisProduct.NOTAM;
  }

  get applyAiracDateFilter(): boolean {
    const applyByProduct = this.isAMDT || this.isSUP;
    const applyByAiracType =
      this.fc.airacType.value && [AiracType.AIRAC, AiracType.DOUBLE_AIRAC].includes(this.fc.airacType.value[0]);
    return applyByProduct && applyByAiracType;
  }

  formValid = toSignal(this.form.statusChanges.pipe(map(() => this.form.valid)), { initialValue: this.form.valid });

  formDirty = toSignal(this.form.valueChanges.pipe(map(() => this.form.dirty)), { initialValue: this.form.dirty });

  constructor() {
    effect(
      () => {
        if (this.formDisabled()) {
          this.form.disable({ emitEvent: false });
        } else {
          this.form.enable({ emitEvent: false });
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this._initUploader();
    this._initCreateFormValues();
    this._initEditFormValues();
    this._getDsProperties();
    this._getValidDate();
    this._getDueDate();
    this._setupTimeValidators();

    // Side-effects to track form fields validity
    this.form.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const fields = Object.keys(this.fc);
      fields.forEach((field) => this.fc[field].updateValueAndValidity({ emitEvent: false }));
    });
  }

  ngAfterViewInit(): void {}

  private _initCreateFormValues(): void {
    if (this.mode() === "new") {
      const temporality = {
        [AisProduct.AMDT]: Temporality.PERMANENT,
        [AisProduct.SUP]: Temporality.TEMPORARY,
        [AisProduct.NOTAM]: Temporality.TEMPORARY,
        [AisProduct.AIC]: Temporality.PERMANENT,
      };

      const airacTypeLookup = {
        [AisProduct.AMDT]: AiracType.NON_AIRAC,
        [AisProduct.SUP]: AiracType.AIRAC,
      };

      const currentDateTime = moment().tz(this.fc.timezone.value);

      this.form.patchValue({
        temporality: [temporality[this.selectedAisProduct.value]],
        airacType: airacTypeLookup[this.selectedAisProduct.value]
          ? [airacTypeLookup[this.selectedAisProduct.value]]
          : null,
        notamType: [NotamType.N],
        // If NOTAM, set effective date to today with current time by default
        effectiveDate: null, //this.isNOTAM ? currentDateTime : null,
      });
      this._syncTimeFromDate("effectiveDate", "effectiveTime");
    }
  }

  private _initEditFormValues(): void {
    this._updateNoticeData
      .asObservable()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        if (!data) return;
        this.form.patchValue(data);
        this._syncTimeFromDate("effectiveDate", "effectiveTime");
        this._syncTimeFromDate("cancelDate", "cancelTime");
        this.fc.effectiveTime.disable({ emitEvent: false });
        this._reloader$.next(true);
      });
  }

  private _pastTimeValidatorFn(dateControlKey: string): ValidatorFn {
    return (): ValidationErrors | null => {
      const dateValue = this.fc[dateControlKey]?.value;
      if (!dateValue) return null;
      const now = moment().tz(this.fc.timezone.value);

      if (dateControlKey === "effectiveDate") {
        return moment(dateValue).tz(this.fc.timezone.value).isBefore(now, "minute") ? { pastTime: true } : null;
      }

      if (dateControlKey === "cancelDate") {
        const effectiveDate = this.fc.effectiveDate.value;
        if (!effectiveDate) return null; // If effective date is not set, we cannot validate cancel date
        const cancelDateTime = moment(dateValue).tz(this.fc.timezone.value);
        return cancelDateTime.isSameOrBefore(moment(effectiveDate).tz(this.fc.timezone.value), "minute")
          ? { pastTime: true }
          : null;
      }
    };
  }

  private _syncTimeFromDate(dateControlKey: string, timeControlKey: string): void {
    const dateValue = this.fc[dateControlKey].value;
    const timeStr = dateValue ? moment(dateValue).tz(this.fc.timezone.value).format("HH:mm") : null;
    this.fc[timeControlKey].setValue(timeStr, { emitEvent: false });
    this.fc[timeControlKey].updateValueAndValidity({ emitEvent: false });
    this.fc[timeControlKey].markAsTouched();
  }

  private _setupTimeValidators(): void {
    combineLatest([
      this.fc.aisProduct.valueChanges.pipe(startWith(this.fc.aisProduct.value)),
      this.fc.temporality.valueChanges.pipe(startWith(this.fc.temporality.value)),
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([product, temporality]) => {
        if (product === AisProduct.NOTAM) {
          this.fc.effectiveTime.setValidators([this._pastTimeValidatorFn("effectiveDate")]);
        } else {
          this.fc.effectiveTime.clearValidators();
        }

        if (temporality?.[0] === Temporality.TEMPORARY) {
          this.fc.cancelTime.setValidators([this._pastTimeValidatorFn("cancelDate")]);
        } else {
          this.fc.cancelTime.clearValidators();
        }

        this.fc.effectiveTime.updateValueAndValidity({ emitEvent: false });
        this.fc.cancelTime.updateValueAndValidity({ emitEvent: false });
      });
  }

  private _getDsProperties(): void {
    this._dsPropsService
      .getDatastoreProperties()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((props) => {
        const properties = props.reduce(
          (accumulator, prop) => {
            if (prop.property === Property.AMDT_NOTICE_PERIOD) accumulator.amdtNoticePeriod = prop.propertyValue;
            if (prop.property === Property.SUP_NOTICE_PERIOD) accumulator.supNoticePeriod = prop.propertyValue;
            if (prop.property === Property.TIMEZONE) accumulator.timezones = prop.propertyValue;
            return accumulator;
          },
          { amdtNoticePeriod: 0, supNoticePeriod: 0, timezones: [] },
        );
        this.amdtNoticePeriod.next(properties["amdtNoticePeriod"] ?? 0);
        this.amdtNonAiracNoticePeriod.next(properties["amdtNoticePeriod"] ?? 0);
        this.amdtDoubleNoticePeriod.next(this.amdtNoticePeriod.value + this._airacInterval);
        this.supNoticePeriod.next(properties["supNoticePeriod"] ?? 0);
        this.timezonePreferences.next(properties["timezones"] ?? []);
      });
  }

  private _getValidDate(): void {
    combineLatest({
      effectiveDate: this.fc.effectiveDate.valueChanges.pipe(startWith(this.fc.effectiveDate.value)),
      aisProduct: this.fc.aisProduct.valueChanges.pipe(startWith(this.fc.aisProduct.value)),
      airacTypes: this.fc.airacType.valueChanges.pipe(startWith(this.fc.airacType.value)),
      timezone: this.fc.timezone.valueChanges.pipe(startWith(this.fc.timezone.value)),
      amdtNoticePeriod: this.amdtNoticePeriod.asObservable(),
      amdtDoubleNoticePeriod: this.amdtDoubleNoticePeriod.asObservable(),
      amdtNonAiracNoticePeriod: this.amdtNonAiracNoticePeriod.asObservable(),
      supNoticePeriod: this.supNoticePeriod.asObservable(),
      reloader: this._reloader$.asObservable(),
    })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        const {
          effectiveDate,
          aisProduct,
          airacTypes,
          amdtDoubleNoticePeriod,
          amdtNoticePeriod,
          supNoticePeriod,
          amdtNonAiracNoticePeriod,
          timezone,
        } = result;
        const applyValidDate = [AisProduct.AMDT, AisProduct.SUP, AisProduct.NOTAM].includes(aisProduct);
        const today = moment().tz(timezone).startOf("day");

        // If not AMDT, SUP or NOTAM, validDate is not applicable
        if (!applyValidDate) {
          this.validDate = null;
          return;
        }

        if (aisProduct === AisProduct.NOTAM) {
          this.validDate = today;
          return;
        }

        if (!result.airacTypes?.length) return;

        const isAMDT = aisProduct === AisProduct.AMDT;
        const lookup = {
          AIRAC: today.clone().add(isAMDT ? amdtNoticePeriod : supNoticePeriod, "days"),
          DOUBLE_AIRAC: today.clone().add(amdtDoubleNoticePeriod, "days"),
          NON_AIRAC: today.clone().add(amdtNonAiracNoticePeriod, "days"),
        };
        const validDate = lookup[airacTypes[0]] as moment.Moment;
        this.validDate = validDate;
        this.startAt = this.getNextAiracDate(validDate);

        if (!effectiveDate) {
          this.fc.effectiveDate.setValidators(Validators.required);
          return;
        }

        const invalidDateFn = () => ({ invalidEffDate: true });

        const isBeforeValidDate = effectiveDate.isBefore(validDate);
        const isAiracDate = this.airacDateFilter(effectiveDate);
        const isAiracTypes = [AiracType.AIRAC, AiracType.DOUBLE_AIRAC].includes(airacTypes[0]);
        const isNotValidAiracDate = isAiracTypes && !isAiracDate;

        if (isBeforeValidDate || isNotValidAiracDate) {
          this.validDate = this.startAt;
          this.fc.effectiveDate.addValidators(invalidDateFn);
        } else {
          this.fc.effectiveDate.setValidators(Validators.required);
        }
      });
  }

  private _getDueDate(): void {
    combineLatest({
      aisProduct: this.fc.aisProduct.valueChanges.pipe(startWith(this.fc.aisProduct.value)),
      effectiveDate: this.fc.effectiveDate.valueChanges.pipe(startWith(this.fc.effectiveDate.value)),
      airacTypes: this.fc.airacType.valueChanges.pipe(startWith(this.fc.airacType.value)),
      timezone: this.fc.timezone.valueChanges.pipe(startWith(this.fc.timezone.value)),
      amdtNoticePeriod: this.amdtNoticePeriod.asObservable(),
      amdtDoubleNoticePeriod: this.amdtDoubleNoticePeriod.asObservable(),
      supNoticePeriod: this.supNoticePeriod.asObservable(),
      amdtNonAiracNoticePeriod: this.amdtNonAiracNoticePeriod.asObservable(),
      reloader: this._reloader$.asObservable(),
    })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        const applyDueDate = [AisProduct.AMDT, AisProduct.SUP].includes(result.aisProduct) && result.effectiveDate;

        if (!applyDueDate) {
          this.dueDate = null;
          this.dueDateChanged.emit(null);
          return;
        }

        if (!result.airacTypes?.length) return;

        const isAMDT = result.aisProduct === AisProduct.AMDT;
        const type = result.airacTypes?.[0];
        const effectiveDate = result.effectiveDate as moment.Moment;
        const lookup = {
          AIRAC: isAMDT ? result.amdtNoticePeriod : result.supNoticePeriod,
          DOUBLE_AIRAC: result.amdtDoubleNoticePeriod,
          NON_AIRAC: result.amdtNonAiracNoticePeriod,
        };
        const noticePeriod = lookup[type] ?? 0;
        const dueDate = effectiveDate
          .clone()
          .subtract(noticePeriod, "days")
          .format("DD MMM YYYY HH:mm A z")
          .toUpperCase();
        this.dueDate = dueDate;
        this.dueDateChanged.emit(dueDate);
      });
  }

  private _initUploader(): void {
    // Upload success
    this.uploader.onAfterAddingFile = (file) => {
      this.attachmentsModified.set(true);
    };
    // Upload failed
    this.uploader.onWhenAddingFileFailed = (item, filter) => {
      const formatBytes = new FormatBytesPipe();
      if (filter.name === "fileSize") {
        this._snackbar.open(
          `File size of ${formatBytes.transform(item.size)} is exceeding maximum file size of ${formatBytes.transform(
            this._maxFileSize,
          )}`,
          "Close",
        );
      }
    };
  }

  onTimeChange(event: Event, controlKey: string): void {
    const input = event.target as HTMLInputElement;
    const [hour, minutes] = input.value.split(":");
    if (!hour || !minutes) return;

    const targetDateControl = this.fc[controlKey];
    const newValue = moment(targetDateControl.value).tz(this.fc.timezone.value).set({ hour: +hour, minutes: +minutes });
    targetDateControl.setValue(newValue);

    const timeControl = controlKey === "effectiveDate" ? this.fc.effectiveTime : this.fc.cancelTime;
    timeControl.markAsTouched();
    timeControl.updateValueAndValidity();
  }

  onEffDateChange(event: Event): void {
    const isToday = moment(event as any).isSame(moment().tz(this.fc.timezone.value), "day");
    if (event && this.isNOTAM && isToday) {
      this.fc.effectiveDate.setValue(moment().tz(this.fc.timezone.value));
    }
    this._syncTimeFromDate("effectiveDate", "effectiveTime");
  }

  onCancelDateChange(): void {
    this._syncTimeFromDate("cancelDate", "cancelTime");
  }

  onAisProductChange(event: MatSelectChange) {
    const value = event.value;
    this.aisProductChanged.emit(value);
  }

  airacDateFilter(date: moment.Moment | null) {
    const referenceAiracDate = moment("2018-11-08");
    // Calculate the difference in days from the reference date
    const diffInDays = date.startOf("day").diff(referenceAiracDate.startOf("day"), "days");
    return diffInDays >= 0 && diffInDays % 28 === 0;
  }

  getNextAiracDate(from: moment.Moment): moment.Moment {
    const referenceAiracDate = moment("2018-11-08").startOf("day");
    const target = from?.clone()?.startOf("day");
    const diffDays = target?.diff(referenceAiracDate, "days");

    // If target is before reference, just return reference
    if (diffDays <= 0) {
      return referenceAiracDate.clone();
    }

    // Calculate next AIRAC using math instead of loop
    const cycles = Math.ceil(diffDays / 28);
    return referenceAiracDate.clone().add(cycles * 28, "days");
  }

  deleteCurrentAttachment(item: string) {
    // Remove from current uploadedFiles
    this.uploadedFiles = this.uploadedFiles.filter((f) => f !== item);

    // Add into filesToBeDeleted only if not already present
    if (!this.filesToBeDeleted.includes(item)) {
      this.filesToBeDeleted.push(item);
      this.attachmentsModified.set(true);
    }
  }

  restoreDeletedAttachment(item: string) {
    // Remove from filesToBeDeleted
    this.filesToBeDeleted = this.filesToBeDeleted.filter((f) => f !== item);

    // Restore into uploadedFiles only if not already present
    if (!this.uploadedFiles.includes(item)) {
      this.uploadedFiles.push(item);
      this.attachmentsModified.set(true);
    }
  }

  downloadAttachement(item: string) {
    this.onAttachmentClicked.emit(item);
  }
}
